import React from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import {
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../../../Account/Register/Register.style";
import { COLORS } from "../../../../shared/theme/colorConfig";

type YesNo = "yes" | "no" | "";

/** Convert stored MM/DD/YYYY (legacy) or ISO to YYYY-MM-DD for <input type="date">. */
const toDateInputValue = (raw?: string): string => {
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const m = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[1]}-${m[2]}`;
  return "";
};

/** DocuSeal prefill expects MM/DD/YYYY on the due_date text field. */
const toDocuSealDate = (isoOrLegacy?: string): string => {
  if (!isoOrLegacy) return "";
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(isoOrLegacy)) return isoOrLegacy;
  const m = isoOrLegacy.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  return isoOrLegacy;
};

export interface SpecialStatusForm {
  elderlyOrDisabled: YesNo;
  // Anticipated household addition (head-only, opt-in). Lets the household be
  // counted at the larger size for income limit / bedroom count. Drives the
  // unborn-child-self-cert e-sign form on the API side.
  anticipatedAddition?: YesNo;
  additionExpecting?: boolean;
  additionAdopting?: boolean;
  additionCustody?: boolean;
  additionDueDate?: string;
  additionExplanation?: string;
}

interface SpecialStatusProps {
  defaultValues?: SpecialStatusForm;
  onSubmit: (data: SpecialStatusForm) => void | Promise<void>;
  onBack: () => void;
  isSaving?: boolean;
  // Only the head-of-household flow collects the anticipated-addition opt-in;
  // co-applicants don't (it's a household-level fact, asked once).
  includeAnticipatedAddition?: boolean;
}

export const SpecialStatus: React.FC<SpecialStatusProps> = ({
  defaultValues,
  onSubmit,
  onBack,
  isSaving = false,
  includeAnticipatedAddition = false,
}) => {
  const { t } = useTranslation();

  const ssAnswerRequired = t(
    "affordable.application.specialStatus.answerRequired"
  ) as string;
  const addBase = "affordable.application.specialStatus.anticipatedAddition";

  const schema = yup.object({
    elderlyOrDisabled: yup
      .string()
      .oneOf(["yes", "no"], ssAnswerRequired)
      .required(ssAnswerRequired),
    anticipatedAddition: includeAnticipatedAddition
      ? yup
          .string()
          .oneOf(["yes", "no"], t(`${addBase}.answerRequired`) as string)
          .required(t(`${addBase}.answerRequired`) as string)
      : yup.string().notRequired(),
    // Attach the "select at least one" rule to the first checkbox so the error
    // renders inline beneath the option group.
    additionExpecting: yup
      .boolean()
      .test(
        "atLeastOne",
        t(`${addBase}.selectAtLeastOne`) as string,
        function (value) {
          if (!includeAnticipatedAddition) return true;
          if (this.parent.anticipatedAddition !== "yes") return true;
          return (
            value === true ||
            this.parent.additionAdopting === true ||
            this.parent.additionCustody === true
          );
        }
      ),
    additionAdopting: yup.boolean(),
    additionCustody: yup.boolean(),
    additionDueDate: yup
      .string()
      .when(["anticipatedAddition", "additionExpecting"], {
        is: (a: YesNo, e: boolean) =>
          includeAnticipatedAddition && a === "yes" && e === true,
        then: (s) =>
          s
            .matches(
              /^\d{4}-\d{2}-\d{2}$/,
              t(`${addBase}.dueDateInvalid`) as string
            )
            .required(t(`${addBase}.dueDateRequired`) as string),
        otherwise: (s) => s.notRequired(),
      }),
    additionExplanation: yup.string().notRequired(),
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<SpecialStatusForm>({
    resolver: yupResolver(schema) as any,
    mode: "onChange",
    defaultValues: {
      elderlyOrDisabled: "",
      anticipatedAddition: "",
      additionExpecting: false,
      additionAdopting: false,
      additionCustody: false,
      additionExplanation: "",
      ...(defaultValues || {}),
      additionDueDate: toDateInputValue(defaultValues?.additionDueDate),
    },
  });

  const anticipatedAddition = watch("anticipatedAddition");
  const additionExpecting = watch("additionExpecting");

  // Normalize before saving: if the opt-in is off or answered No, clear the
  // sub-fields so a stale "true" can't trigger the cert on the API side.
  const submit = (data: SpecialStatusForm) => {
    let out = data;
    if (!includeAnticipatedAddition || data.anticipatedAddition !== "yes") {
      out = {
        ...data,
        additionExpecting: false,
        additionAdopting: false,
        additionCustody: false,
        additionDueDate: "",
        additionExplanation: "",
      };
    } else if (!data.additionExpecting) {
      out = { ...data, additionDueDate: "" };
    } else {
      out = {
        ...data,
        additionDueDate: toDocuSealDate(data.additionDueDate),
      };
    }
    return onSubmit(out);
  };

  const YES_NO_OPTIONS: { value: "yes" | "no"; labelKey: "yes" | "no" }[] = [
    { value: "yes", labelKey: "yes" },
    { value: "no", labelKey: "no" },
  ];

  return (
    <form onSubmit={handleSubmit(submit)}>
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.specialStatus.title")}
        </RegisterReviewSectionTitle>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {t("affordable.application.specialStatus.question")}
        </Typography>

        <Controller
          name="elderlyOrDisabled"
          control={control}
          render={({ field }) => (
            <FormControl
              component="fieldset"
              fullWidth
              error={!!errors.elderlyOrDisabled}
            >
              <RadioGroup {...field}>
                {YES_NO_OPTIONS.map((opt) => (
                  <Box
                    key={opt.value}
                    sx={{
                      border: `1px solid ${
                        field.value === opt.value
                          ? COLORS.darkBlue[500]
                          : COLORS.gray[200]
                      }`,
                      borderRadius: 2,
                      p: 2,
                      mb: 1.5,
                      backgroundColor:
                        field.value === opt.value
                          ? COLORS.blue[50]
                          : "transparent",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <FormControlLabel
                      value={opt.value}
                      control={<Radio />}
                      sx={{
                        m: 0,
                        "& .MuiFormControlLabel-label": { flex: 1 },
                      }}
                      label={
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {t(
                            `affordable.application.specialStatus.${opt.labelKey}`
                          )}
                        </Typography>
                      }
                    />
                  </Box>
                ))}
              </RadioGroup>
              {errors.elderlyOrDisabled && (
                <FormHelperText>
                  {errors.elderlyOrDisabled.message}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />
      </RegisterReviewSection>

      {includeAnticipatedAddition && (
        <RegisterReviewSection>
          <RegisterReviewSectionTitle>
            {t(`${addBase}.title`)}
          </RegisterReviewSectionTitle>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {t(`${addBase}.question`)}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 2 }}
          >
            {t(`${addBase}.helper`)}
          </Typography>

          <Controller
            name="anticipatedAddition"
            control={control}
            render={({ field }) => (
              <FormControl
                component="fieldset"
                fullWidth
                error={!!errors.anticipatedAddition}
              >
                <RadioGroup {...field} value={field.value || ""}>
                  {YES_NO_OPTIONS.map((opt) => (
                    <Box
                      key={opt.value}
                      sx={{
                        border: `1px solid ${
                          field.value === opt.value
                            ? COLORS.darkBlue[500]
                            : COLORS.gray[200]
                        }`,
                        borderRadius: 2,
                        p: 2,
                        mb: 1.5,
                        backgroundColor:
                          field.value === opt.value
                            ? COLORS.blue[50]
                            : "transparent",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <FormControlLabel
                        value={opt.value}
                        control={<Radio />}
                        sx={{
                          m: 0,
                          "& .MuiFormControlLabel-label": { flex: 1 },
                        }}
                        label={
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {t(
                              `affordable.application.specialStatus.${opt.labelKey}`
                            )}
                          </Typography>
                        }
                      />
                    </Box>
                  ))}
                </RadioGroup>
                {errors.anticipatedAddition && (
                  <FormHelperText>
                    {errors.anticipatedAddition.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />

          {anticipatedAddition === "yes" && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}
            >
              <Controller
                name="additionExpecting"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={t(`${addBase}.expectingLabel`)}
                  />
                )}
              />
              {additionExpecting && (
                <Controller
                  name="additionDueDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ""}
                      type="date"
                      fullWidth
                      size="small"
                      label={t(`${addBase}.dueDateLabel`)}
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.additionDueDate}
                      helperText={errors.additionDueDate?.message}
                      sx={{ ml: 4, mb: 1, maxWidth: 280 }}
                    />
                  )}
                />
              )}
              <Controller
                name="additionAdopting"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={t(`${addBase}.adoptingLabel`)}
                  />
                )}
              />
              <Controller
                name="additionCustody"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={t(`${addBase}.custodyLabel`)}
                  />
                )}
              />
              {errors.additionExpecting && (
                <FormHelperText error>
                  {errors.additionExpecting.message}
                </FormHelperText>
              )}
              <Controller
                name="additionExplanation"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value || ""}
                    fullWidth
                    multiline
                    rows={3}
                    label={t(`${addBase}.explanationLabel`)}
                    placeholder={
                      t(`${addBase}.explanationPlaceholder`) as string
                    }
                    sx={{ mt: 1 }}
                  />
                )}
              />
            </Box>
          )}
        </RegisterReviewSection>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 3,
          mb: 4,
        }}
      >
        <Button variant="outlined" onClick={onBack} disabled={isSaving}>
          {t("account.leaseApplication.previous")}
        </Button>
        <Button
          variant="contained"
          type="submit"
          disabled={isSaving || !isValid}
          startIcon={
            isSaving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {t("account.leaseApplication.next")}
        </Button>
      </Box>
    </form>
  );
};
